import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { parseISO, isBefore, startOfMonth, endOfMonth, format, addHours, formatISO } from 'date-fns';
import TitleRecordFunction from "../../components/TitleRecordFunction";
import api from '../../api/manage_database/FlowMeterReq';
import apii from '../../api/manage_database/FlowMeasurementReq';
import BackButton from "../../components/BackButton";
import Spinner from "../../components/Spinner";
import { toast } from 'sonner';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import SelectPlace from "../../components/SelectPlace";
import SelectThoroughfare from "../../components/SelectThroughfare";
import SelectTypeLines from "../../components/SelectTypeLines";
import DateInput from "../../components/InputDate";
import SelectParameter from "../../components/SelectParameter";
import SelectInterval from "../../components/SelectInterval";
import SelectTime from "../../components/SelectTime";
import { FaTrashCan } from "react-icons/fa6";
import { FcOk } from "react-icons/fc";
import { MdCancel } from "react-icons/md";

const FlowMeasurementRecord = () => {

    const [loadingState, setLoadingState] = useState(false);

    const [flowMeters, setFlowMeters] = useState([]);

    const [selectedPlace, setSelectedPlace] = useState('');
    const [places, setPlaces] = useState([]);
    const [selectedThoroughfare, setSelectedThoroughfare] = useState('');
    const [thoroughfares, setThoroughfares] = useState([]);
    const [selectedTypeLine, setSelectedTypeLine] = useState('');
    const [typeLines, setTypeLines] = useState([]);
    const initialIntervals = [1, 2, 4, 6, 12, 24]
    const [intervals, setIntervals] = useState(initialIntervals);
    const [selectedInterval, setSelectedInterval] = useState(intervals[4]);
    const [deleteDateAndHour, setDeleteDateAndHour] = useState();
    const [deletingAll, setDeletingAll] = useState(true);

    const chartOrder = [
        'Flow',
        'DT',
        'TTUp',
        'TTDn',
        'FlowVelocity',
        'AbsoluteSoundVelocity',
        'SoundVelocity',
        'AbsoluteRe',
        'Re',
        'SignalAmplitudeUp',
        'SignalAmplitudeDn',
        'RxGainUp',
        'RxGainDn',
        'SNRUp',
        'SNRDn',
        'CorrelationCoef',
        'ReferenceCoef',
        'PcbTemp',
        'ExternalInput',
        'BurstsAccepted',
        'Temp',
        'StDev',
        'MeanFlow',
        'MeanDT',
        'StdFlow',
        'StdDT',
        'DiffStdFlow',
    ];

    const [chartName, setChartName] = useState(chartOrder[0]);

    const firstDay = startOfMonth(new Date());
    const formattedFirstDay = format(firstDay, 'yyyy-MM-dd');
    const lastDay = endOfMonth(new Date());
    const formattedLastDay = format(lastDay, 'yyyy-MM-dd');

    const [selectedFromDate, setSelectedFromDate] = useState(formattedFirstDay);
    const [selectedToDate, setSelectedToDate] = useState(formattedLastDay);

    const hours = Array.from({ length: 24 }, (_, i) => {
        const h = String(i).padStart(2, '0');
        return `${h}:00`;
    });
    const [times, setTimes] = useState(hours);
    const [selectedFromTime, setSelectedFromTime] = useState('00:00');
    const [selectedToTime, setSelectedToTime] = useState('00:00');
    const navigate = useNavigate();

    const handleChangeFromDate = (event) => {
        setSelectedFromDate(event.target.value)
    };
    const handleChangeToDate = (event) => {
        setSelectedToDate(event.target.value)
    };

    const handleAction = (date, hour, toastId) => {
        setDeleteDateAndHour({
            date: date,
            hour: hour
        })
        toast.dismiss(toastId);
    }

    const handleCancel = (toastId) => {
        toast.warning(
            'Operation cancelled!',
            {
                id: toastId,
                action: null,
                cancel: null,
                duration: 5000
            }
        );
    }

    const handleDelete = (date, hour) => {
        const msg = `Are you sure to delete: ${deletingAll? 'all parameters': chartName} for ${date} ${hour} for ${selectedInterval}h?`
        const toastId = toast.warning(msg, {
            style: {width: '20rem'},
            action: <FcOk onClick={() => handleAction(date, hour, toastId)} style={{fontSize: '4rem'}}/>,
            cancel: <MdCancel onClick={() => handleCancel(toastId)} style={{fontSize: '4rem', color: 'red'}}/>,
            duration: 10000
        });
    }    

    const [data, setData] = useState({})

    const handleFindButton = () => {
        const areDatesAndTimesSet = selectedFromDate &&
                                    selectedFromTime &&
                                    selectedToDate &&
                                    selectedToTime

        if (!areDatesAndTimesSet) {
            toast.error('Error! Insert all dates and times!');
            return
        }

        const dateTimeFromBeforeParsing = `${selectedFromDate}T${selectedFromTime}:00`
        const dateTimeToBeforeParsing = `${selectedToDate}T${selectedToTime}:00`

        const dateTimeFromParsed = parseISO(dateTimeFromBeforeParsing)
        const dateTimeToParsed = parseISO(dateTimeToBeforeParsing)

        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            toast.error('Error! First datetime is not earlier!')
            return
        }

        const filteredFlowMeterId = flowMeters
                                        .filter(
                                            flowmeter => flowmeter.location_id.place === selectedPlace &&
                                            flowmeter.location_id.thoroughfare === selectedThoroughfare && 
                                            flowmeter.location_id.type_line === selectedTypeLine)
                                        .map((flowmeter) => (flowmeter.id))[0]

        const fetchAvailableMeasurements = async () => {
            setLoadingState(true);
            try {
                const response = await apii.get(
                    `/available-measurements/${filteredFlowMeterId}/${chartName}/${selectedInterval}/${dateTimeFromBeforeParsing}/${dateTimeToBeforeParsing}`,
                    {withCredentials: true}
                );
                if (response.data.success) {
                    setTimes([...Array(24).keys()].filter(t => t % selectedInterval === 0).map(hour => {
                        return `${hour.toString().padStart(2, '0')}:00`
                    }));
                    setData(response.data.payload);
                } else {
                    console.error('Error:', response.data.message)
                }
            } catch (err) {
                console.log(err.response);
            }
            setLoadingState(false);
        };
        fetchAvailableMeasurements();   
    }

    const handleChangeDeletingAll = () => {
        setDeletingAll(prev => !prev);
        toast.warning(`Changed to deleting ${deletingAll ? 'one parameter': 'all parameters'}`);
    }

    useEffect(() => {
        if (deleteDateAndHour) {
             const filteredFlowMeterId = flowMeters
                                        .filter(
                                            flowmeter => flowmeter.location_id.place === selectedPlace &&
                                            flowmeter.location_id.thoroughfare === selectedThoroughfare && 
                                            flowmeter.location_id.type_line === selectedTypeLine)
                                        .map((flowmeter) => (flowmeter.id))[0]

            const [day, month, year] = deleteDateAndHour.date.split("-");
            const dateTimeFromBeforeParsing = `${year}-${month}-${day}T${deleteDateAndHour.hour}:00`
            const dateTimeFromParsed = parseISO(dateTimeFromBeforeParsing);
            const added = addHours(dateTimeFromParsed, selectedInterval);
            const datePlusIntervalParsed = formatISO(added, { representation: 'complete' }).slice(0, 19);

            const deleteFlowMeasurement = async (parameter) => {
                setLoadingState(true);
                try {
                    const response = await apii.delete(
                        `/delete/${filteredFlowMeterId}/${parameter}/${dateTimeFromBeforeParsing}/${datePlusIntervalParsed}`,
                        {withCredentials: true}
                    );
                    if (response.data.success) {
                        toast.success(`Successfully deleted ${parameter} ${dateTimeFromBeforeParsing}-${datePlusIntervalParsed}`)
                    } else {
                        console.error('Error:', response.data.message)
                    }
                } catch (err) {
                    console.log(err.response);
                }
                setLoadingState(false);
            }
            if (deletingAll) {
                for (const name of chartOrder) {
                    deleteFlowMeasurement(name);
                }
            } else {
                deleteFlowMeasurement(chartName);
            }
            setDeleteDateAndHour();
            setData({});

        }
    }, [deleteDateAndHour])

    useEffect(() => {
        setTimes([...Array(24).keys()].filter(t => t % selectedInterval === 0).map(hour => {
            return `${hour.toString().padStart(2, '0')}:00`
        }))
        setData({});
    }, [selectedInterval])

    useEffect(() => {
        const fetchFlowMeters = async () => {
            try {
                const response = await api.get('all', {withCredentials: true});
                if (response.data.success) {
                    setFlowMeters(response.data.payload);
                } else {
                    console.error('Error:', response.data.message)
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchFlowMeters();
    }, []);
    
    useEffect(() => {
        const handlePlaces = () => {
            const newplaces = [...new Set(flowMeters.map(item => item.location_id.place))] 
            return newplaces.map((name, index) => ({id: index+1, name: name}));
            
        }
        if (flowMeters.length > 0) {
            setPlaces(handlePlaces());
        }
    }, [flowMeters])

    useEffect(() => {
        const filterPlaces = () => {
            const newthoroughfare = flowMeters.filter(flowmeter => flowmeter.location_id.place === selectedPlace).map((fl, index) => ({id: index+1, name: fl.location_id.thoroughfare}))
            return newthoroughfare.reduce((acc, current) => {
                if (!acc.some(item => item.name === current.name)) {
                    acc.push(current);
                }
                return acc
            }, [])
        }
        if (flowMeters.length > 0) {
            setThoroughfares(filterPlaces())
        }
    }, [selectedPlace])

    useEffect(() => {
        const filterPlacesAndThoroughfares = () => {
            const newTypeLines = flowMeters
                                    .filter(flowmeter => flowmeter.location_id.place === selectedPlace && flowmeter.location_id.thoroughfare === selectedThoroughfare)
                                    .map((fl, index) => ({id: index+1, name: fl.location_id.type_line}))
            
            return newTypeLines.reduce((acc, current) => {
                if (!acc.some(item => item.name === current.name)) {
                    acc.push(current);
                }
                return acc
            }, [])
        }
        if (flowMeters.length > 0) {
            setTypeLines(filterPlacesAndThoroughfares())
        }
    }, [selectedThoroughfare]);

   return (
        <div className="flow-measurement-record-container">
            <TitleRecordFunction
                addRecordMessage={""}
                deleteRecordMessage={""}
                title='Flow Measurement'
                isViewRecord={true}
            />
            <div className="flow-measurement-record-card">
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        Place:
                    </label>
                    <SelectPlace
                        places={places}
                        selectedPlace={selectedPlace}
                        setSelectedPlace={setSelectedPlace}
                    />
                </div>
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        Thoroughfare:
                    </label>
                    <SelectThoroughfare
                        thoroughfares={thoroughfares}
                        selectedThoroughfare={selectedThoroughfare}
                        setSelectedThoroughfare={setSelectedThoroughfare}
                    />
                </div>
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        Type Line:
                    </label>
                    <SelectTypeLines
                        LocationTypeLines={typeLines}
                        selectedlocationTypeLine={selectedTypeLine}
                        setSelectedLocationTypeLine={setSelectedTypeLine}
                    />
                </div>
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        Param:
                    </label>
                    <SelectParameter
                        Parameters={chartOrder}
                        selectedParameter={chartName}
                        setSelectedParameter={setChartName}
                    />
                </div>
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        Interval [h]:
                    </label>
                    <SelectInterval
                        intervals={intervals}
                        selectedInterval={selectedInterval}
                        setSelectedInterval={setSelectedInterval}
                    />
                </div>
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        FROM:
                    </label>
                    <div className="choice-date-time-group">
                        <DateInput 
                            selectedDate={selectedFromDate}
                            handleSelectedDate={handleChangeFromDate}
                        />
                        <SelectTime 
                            times={times}
                            selectedTime={selectedFromTime}
                            setSelectedTime={setSelectedFromTime}
                        />
                    </div>
                </div>
                <div className="choice-input-group">
                    <label
                        htmlFor=''
                        className='bold-label'
                    >
                        TO:
                    </label>
                    <div className="choice-date-time-group">
                        <DateInput 
                            selectedDate={selectedToDate}
                            handleSelectedDate={handleChangeToDate}
                        />
                        <SelectTime 
                            times={times}
                            selectedTime={selectedToTime}
                            setSelectedTime={setSelectedToTime}
                        />
                    </div>
                </div>
                {!loadingState ?<button className='menu-button' onClick={handleFindButton}>
                    Find <FaMagnifyingGlass />
                </button> : <button className='menu-button'>
                    <Spinner size="small" />
                </button>}
                <button
                    className='menu-button'
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => navigate('/manage-database/flow-measurement/sample')}
                >
                    Sample view
                </button>
            </div>
            <button
                className='flow-measurement-record-button-changing'
                onClick={handleChangeDeletingAll}
            >
                Change to deleting {deletingAll ? 'one parameter': 'all parameters'}
            </button>
            {!loadingState ? <div className="table-wrapper">
                <table className="flow-measurement-record-table-style text-align-center">
                    <colgroup>
                        <col className="spec-width" />
                        {times.map((hour) => {
                            return <col className="td-width" />
                        })}
                    </colgroup>
                    <tbody>
                        <tr>
                            <td>Date / Time</td>
                            {times.map((t) => {
                                return <td>{t}</td>
                            })}
                        </tr>
                        {Object.entries(data).map(([date, hours]) => (
                        <tr key={date}>
                            <td>{date}</td>
                            {times.map(hour => (
                            hours[hour] ?
                                <td key={hour} className="td-green">
                                    <FaTrashCan 
                                        onClick={() => handleDelete(date, hour)}
                                    />
                                </td> :
                                <td key={hour} className="td-red">
                                </td>
                            ))}
                        </tr>
                        ))}
                    </tbody>
                </table>
            </div>:
            null}
            <BackButton navigateTo='/manage-database'/>
        </div>
    );
}

export default FlowMeasurementRecord;